import { MdClose, MdCheckCircle, MdArrowForward, MdShoppingCart, MdChevronLeft, MdChevronRight } from 'react-icons/md';

type BodyEntry = { field: string; answer: string };
export type PurchasedLead = {
    _id: string;
    industry?: string;
    subIndustry?: string;
    body?: BodyEntry[];
    leadrsRate?: number;
    email?: string;
    gdprConsent?: { accepted?: boolean };
};

const PurchaseSuccessModal = ({
    isOpen,
    onClose,
    lead,
    pricePaid,
    onViewMyLeads,
    onContinueCategory,
    currentIndex,
    totalCount,
    onPrev,
    onNext,
}: {
    isOpen: boolean;
    onClose: () => void;
    lead: PurchasedLead | null;
    pricePaid: number | null;
    onViewMyLeads: () => void;
    onContinueCategory: () => void;
    // Only set (and rendered) when a batch purchase bought more than one lead — lets the
    // buyer browse every lead just bought in that run inside this same modal.
    currentIndex?: number;
    totalCount?: number;
    onPrev?: () => void;
    onNext?: () => void;
}) => {
    if (!isOpen || !lead) return null;

    const isMulti = !!totalCount && totalCount > 1;
    const index = currentIndex ?? 0;

    const bodyEntries = (lead.body || []).filter((e) => e.field && e.answer);

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/30 p-4 backdrop-blur-sm">
            <div className="relative z-[1002] w-full max-w-[520px]">
                <div className="relative rounded-2xl border border-leadrs-stroke bg-white shadow-lg">
                    <button className="absolute right-4 top-4" onClick={onClose} aria-label="Fermer">
                        <MdClose size={20} className="text-leadrs-text-soft transition-colors hover:text-leadrs-text" />
                    </button>

                    {isMulti && (
                        <div className="flex items-center justify-between gap-2 border-b border-leadrs-stroke px-8 py-3">
                            <button
                                onClick={onPrev}
                                disabled={index <= 0}
                                aria-label="Lead précédent"
                                className="flex h-7 w-7 items-center justify-center rounded-lg border border-leadrs-stroke bg-white text-leadrs-text-soft transition hover:bg-leadrs-bg disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                <MdChevronLeft size={18} />
                            </button>
                            <span className="font-geist text-xs font-semibold text-leadrs-text-soft">
                                Lead {index + 1} / {totalCount}
                            </span>
                            <button
                                onClick={onNext}
                                disabled={index >= (totalCount ?? 1) - 1}
                                aria-label="Lead suivant"
                                className="flex h-7 w-7 items-center justify-center rounded-lg border border-leadrs-stroke bg-white text-leadrs-text-soft transition hover:bg-leadrs-bg disabled:cursor-not-allowed disabled:opacity-40"
                            >
                                <MdChevronRight size={18} />
                            </button>
                        </div>
                    )}

                    <div className="flex flex-col items-center gap-3 border-b border-leadrs-stroke px-8 pb-6 pt-8">
                        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-leadrs-green-soft">
                            <MdCheckCircle className="h-7 w-7 text-leadrs-green-focus" />
                        </div>
                        <p className="text-center font-crimson text-2xl font-semibold text-leadrs-text">
                            {isMulti ? `${totalCount} leads achetés avec succès !` : 'Lead acheté avec succès !'}
                        </p>
                        <p className="text-center font-geist text-sm text-leadrs-text-soft">
                            {pricePaid != null ? `${pricePaid.toFixed(2)} € ont été débités de votre solde pour ce lead. ` : ''}
                            Il est maintenant visible dans vos leads, en attente de vérification.
                        </p>
                    </div>

                    <div className="space-y-4 px-8 py-6">
                        <div className="flex flex-wrap items-center gap-2">
                            {lead.subIndustry && (
                                <span className="rounded-full bg-leadrs-purple-soft px-2.5 py-1 font-geist text-xs font-semibold text-leadrs-purple-focus">
                                    {lead.subIndustry}
                                </span>
                            )}
                            {lead.industry && (
                                <span className="font-geist text-xs text-leadrs-text-soft">{lead.industry}</span>
                            )}
                            {lead.leadrsRate != null && (
                                <span className="ml-auto rounded-full border border-leadrs-stroke px-2.5 py-1 font-geist text-xs font-semibold text-leadrs-text">
                                    Score {lead.leadrsRate}/10
                                </span>
                            )}
                        </div>

                        {bodyEntries.length > 0 && (
                            <div className="rounded-xl border border-leadrs-stroke bg-leadrs-bg p-4">
                                <p className="mb-2 font-geist text-[10px] font-semibold uppercase tracking-widest text-leadrs-text-soft">
                                    Coordonnées du lead
                                </p>
                                <dl className="space-y-1.5">
                                    {bodyEntries.map((entry, i) => (
                                        <div key={i} className="flex items-baseline justify-between gap-3">
                                            <dt className="font-geist text-xs text-leadrs-text-soft">{entry.field}</dt>
                                            <dd className="truncate font-geist text-sm font-medium text-leadrs-text">{entry.answer}</dd>
                                        </div>
                                    ))}
                                    {lead.email && (
                                        <div className="flex items-baseline justify-between gap-3">
                                            <dt className="font-geist text-xs text-leadrs-text-soft">Email</dt>
                                            <dd className="truncate font-geist text-sm font-medium text-leadrs-text">{lead.email}</dd>
                                        </div>
                                    )}
                                </dl>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col gap-2 border-t border-leadrs-stroke px-8 py-6 sm:flex-row">
                        <button
                            onClick={onContinueCategory}
                            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-leadrs-stroke bg-white px-4 py-2.5 font-geist text-sm font-semibold text-leadrs-text transition hover:bg-leadrs-bg"
                        >
                            <MdShoppingCart size={16} />
                            Continuer à acheter
                        </button>
                        <button
                            onClick={onViewMyLeads}
                            className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-leadrs-brand px-4 py-2.5 font-geist text-sm font-semibold text-white transition hover:opacity-90"
                        >
                            Voir mes leads
                            <MdArrowForward size={16} />
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PurchaseSuccessModal;
